/**
 * @fileoverview Modal de edicao de template existente.
 *
 * Layout: 2 abas
 *   - "Editar": altera apenas os components (Meta nao permite mudar name/category/language)
 *   - "Estatisticas": agregados dos ultimos 30 dias (sent/delivered/read/clicked)
 *
 * Sempre renderiza preview a direita.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  X,
  Check,
  BarChart3,
  Pencil,
  Send,
  Eye,
  CheckCheck,
  MousePointerClick,
  RefreshCw,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useModalStyles } from "./modalStyles";
import {
  CATEGORY_LABELS,
  LANGUAGE_LABELS,
  getStatusLabel,
  getStatusStyle,
} from "./statusConfig";
import {
  buildComponentsPayload,
  extractVars,
  parseComponentsToFlat,
  validateBody,
} from "./componentsHelpers";
import TemplatePreview from "./TemplatePreview";
import MediaHeaderUploader from "./MediaHeaderUploader";

const useAnalyticsStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    metricsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: theme.spacing(1.5),
    },
    metricCard: {
      padding: theme.spacing(1.75),
      borderRadius: 12,
      background: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.5),
    },
    metricHead: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.74rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: isDark ? "rgba(255,255,255,0.5)" : "#64748b",
    },
    metricValue: {
      fontSize: "1.75rem",
      fontWeight: 700,
      color: isDark ? "#FFFFFF" : "#0f172a",
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1,
    },
    metricRatio: {
      fontSize: "0.76rem",
      color: isDark ? "rgba(255,255,255,0.45)" : "#94a3b8",
    },
    chartWrap: {
      padding: theme.spacing(2),
      borderRadius: 12,
      background: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
    },
    chartTitle: {
      fontSize: "0.8rem",
      fontWeight: 600,
      color: isDark ? "rgba(255,255,255,0.75)" : "#334155",
      marginBottom: theme.spacing(1.5),
    },
    chartSvg: {
      width: "100%",
      height: 160,
      display: "block",
    },
    legend: {
      display: "flex",
      gap: theme.spacing(2),
      marginTop: theme.spacing(1.25),
      flexWrap: "wrap",
    },
    legendItem: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.76rem",
      color: isDark ? "rgba(255,255,255,0.6)" : "#475569",
    },
    legendDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
    },
    muted: {
      fontSize: "0.82rem",
      color: isDark ? "rgba(255,255,255,0.5)" : "#64748b",
      padding: theme.spacing(2),
      textAlign: "center",
    },
    rangePill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: "0.78rem",
      fontWeight: 500,
      background: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
      color: isDark ? "rgba(255,255,255,0.7)" : "#475569",
    },
  };
});

const METRIC_CONFIG = [
  { key: "sent", label: "Enviados", Icon: Send, color: "#6366f1" },
  { key: "delivered", label: "Entregues", Icon: CheckCheck, color: "#10b981" },
  { key: "read", label: "Lidos", Icon: Eye, color: "#f59e0b" },
  { key: "clicked", label: "Cliques", Icon: MousePointerClick, color: "#ec4899" },
];

/**
 * Normaliza a resposta /template_analytics para um shape simples.
 * Response: { data: [{ data_points: [{ template_id, start, end, sent, delivered, read, clicked }] }] }
 */
const summarizeAnalytics = (raw) => {
  const totals = { sent: 0, delivered: 0, read: 0, clicked: 0 };
  const byDay = new Map(); // start -> { sent, delivered, read, clicked }

  const points = raw?.data?.[0]?.data_points || [];
  points.forEach((p) => {
    const day = p.start;
    const acc = byDay.get(day) || { sent: 0, delivered: 0, read: 0, clicked: 0 };
    ["sent", "delivered", "read", "clicked"].forEach((k) => {
      const v = Number(p[k] || 0);
      acc[k] += v;
      totals[k] += v;
    });
    byDay.set(day, acc);
  });

  const series = Array.from(byDay.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([start, v]) => ({ start, ...v }));

  return { totals, series };
};

const LineChart = ({ series, width = 480, height = 160 }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  if (!series || series.length < 2) return null;

  const padX = 14;
  const padY = 20;
  const maxVal =
    Math.max(
      1,
      ...series.flatMap((d) => [d.sent, d.delivered, d.read, d.clicked])
    ) * 1.1;

  const xStep = (width - padX * 2) / (series.length - 1);

  const toPath = (key) => {
    return series
      .map((d, i) => {
        const x = padX + i * xStep;
        const y = height - padY - (d[key] / maxVal) * (height - padY * 2);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
      <line
        x1={padX}
        x2={width - padX}
        y1={height - padY}
        y2={height - padY}
        stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}
        strokeWidth={1}
      />
      {METRIC_CONFIG.map((m) => (
        <path
          key={m.key}
          d={toPath(m.key)}
          fill="none"
          stroke={m.color}
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

const TemplateEditModal = ({ open, template, connectionId, onClose, onSuccess }) => {
  const classes = useModalStyles();
  const analyticsClasses = useAnalyticsStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const [tab, setTab] = useState(template?.initialTab || "edit");
  const [state, setState] = useState(() =>
    parseComponentsToFlat(template?.components || [])
  );
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  useEffect(() => {
    if (template) {
      setState(parseComponentsToFlat(template.components || []));
      setTab(template.initialTab || "edit");
    }
  }, [template]);

  const previewComponents = useMemo(() => {
    return buildComponentsPayload({
      header: state.header,
      body: state.body,
      footer: state.footer,
      buttons: state.buttons,
    });
  }, [state]);

  const bodyError = validateBody(state.body.text);
  const canSave = !bodyError;

  const updateBody = (patch) =>
    setState((s) => ({ ...s, body: { ...s.body, ...patch } }));
  const updateFooter = (patch) =>
    setState((s) => ({ ...s, footer: { ...s.footer, ...patch } }));
  const updateHeader = (patch) =>
    setState((s) => ({ ...s, header: { ...s.header, ...patch } }));

  const handleSave = async () => {
    if (!connectionId || !template?.id) return;
    setSaving(true);
    try {
      await api.put(`/meta/templates/${connectionId}/${template.id}`, {
        components: previewComponents,
        category: template.category,
        name: template.name,
        language: template.language,
      });
      toast.success("Template atualizado.");
      if (onSuccess) onSuccess();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const loadAnalytics = async () => {
    if (!template?.id || !connectionId) return;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const end = Math.floor(Date.now() / 1000);
      const start = end - 30 * 24 * 60 * 60;
      const { data } = await api.get(
        `/meta/templates/${connectionId}/analytics/summary`,
        {
          params: {
            start,
            end,
            ids: template.id,
            granularity: "DAILY",
          },
        }
      );
      setAnalytics(summarizeAnalytics(data));
    } catch (err) {
      setAnalyticsError(
        err?.response?.data?.error ||
          err?.message ||
          "Não foi possível carregar estatísticas"
      );
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "analytics" && !analytics && !analyticsLoading) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!template) return null;

  const statusStyle = getStatusStyle(template.status, isDark);
  const bodyVars = extractVars(state.body.text);

  const computeRatio = (num, den) =>
    den > 0 ? `${((num / den) * 100).toFixed(1)}%` : "—";

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <div className={classes.dialogInner}>
        <div className={classes.formCol}>
          <div className={classes.header}>
            <div className={classes.eyebrow}>Editar template</div>
            <div
              className={classes.title}
              style={{
                fontFamily:
                  "ui-monospace, 'SF Mono', 'Menlo', monospace",
                fontSize: "1.2rem",
              }}
            >
              {template.name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              {statusStyle && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 9px",
                    borderRadius: 999,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    background: statusStyle.bg,
                    color: statusStyle.fg,
                    border: `1px solid ${statusStyle.border}`,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: statusStyle.fg,
                    }}
                  />
                  {getStatusLabel(template.status)}
                </span>
              )}
              <span className={classes.subtitle} style={{ fontSize: "0.8rem" }}>
                {CATEGORY_LABELS[template.category] || template.category} ·{" "}
                {LANGUAGE_LABELS[template.language] || template.language}
              </span>
            </div>
            <button
              type="button"
              className={classes.closeBtn}
              onClick={onClose}
              disabled={saving}
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>

          <div className={classes.tabs}>
            <button
              type="button"
              className={`${classes.tab} ${
                tab === "edit" ? classes.tabActive : ""
              }`}
              onClick={() => setTab("edit")}
            >
              <Pencil size={13} strokeWidth={1.5} /> Editar
            </button>
            <button
              type="button"
              className={`${classes.tab} ${
                tab === "analytics" ? classes.tabActive : ""
              }`}
              onClick={() => setTab("analytics")}
            >
              <BarChart3 size={13} strokeWidth={1.5} /> Estatísticas
            </button>
          </div>

          <div className={classes.body}>
            {tab === "edit" ? (
              <>
                <div className={classes.hint}>
                  A Meta permite atualizar apenas o conteúdo (cabeçalho, corpo,
                  rodapé e botões). Nome, categoria e idioma são imutáveis.
                </div>

                {state.header.type === "TEXT" && (
                  <div className={classes.fieldGroup}>
                    <div className={classes.label}>Cabeçalho</div>
                    <input
                      className={classes.input}
                      value={state.header.text}
                      onChange={(e) =>
                        updateHeader({ text: e.target.value.slice(0, 60) })
                      }
                      maxLength={60}
                    />
                    <div className={classes.counter}>
                      {state.header.text.length}/60
                    </div>
                  </div>
                )}

                {(state.header.type === "IMAGE" ||
                  state.header.type === "VIDEO" ||
                  state.header.type === "DOCUMENT") && (
                  <div className={classes.fieldGroup}>
                    <div className={classes.label}>
                      Cabeçalho ({state.header.type})
                    </div>
                    <MediaHeaderUploader
                      format={state.header.type}
                      header={state.header}
                      connectionId={connectionId}
                      onChange={(patch) => updateHeader(patch)}
                    />
                    <div className={classes.hint}>
                      Trocar a mídia exige re-aprovação da Meta (~24-48h). O
                      template volta para o status PENDING durante a análise.
                    </div>
                  </div>
                )}

                <div className={classes.fieldGroup}>
                  <div className={classes.label}>Corpo</div>
                  <textarea
                    className={`${classes.input} ${classes.textarea}`}
                    value={state.body.text}
                    onChange={(e) =>
                      updateBody({ text: e.target.value.slice(0, 1024) })
                    }
                    maxLength={1024}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div className={classes.hint}>
                      {bodyVars.length > 0
                        ? `Variáveis: ${bodyVars.map((v) => `{{${v}}}`).join(", ")}`
                        : "Use {{1}}, {{2}} para valores dinâmicos."}
                    </div>
                    <div className={classes.counter}>
                      {state.body.text.length}/1024
                    </div>
                  </div>
                  {bodyError && <div className={classes.error}>{bodyError}</div>}
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.label}>Rodapé</div>
                  <input
                    className={classes.input}
                    value={state.footer.text}
                    onChange={(e) =>
                      updateFooter({ text: e.target.value.slice(0, 60) })
                    }
                    maxLength={60}
                    placeholder="(opcional)"
                  />
                </div>

                {template.rejected_reason &&
                  template.rejected_reason !== "NONE" && (
                  <div
                    className={classes.reviewSection}
                    style={{
                      borderColor: isDark
                        ? "rgba(239,68,68,0.25)"
                        : "rgba(239,68,68,0.2)",
                      background: isDark
                        ? "rgba(239,68,68,0.08)"
                        : "rgba(239,68,68,0.04)",
                    }}
                  >
                    <div
                      className={classes.reviewLabel}
                      style={{
                        color: isDark ? "#fca5a5" : "#b91c1c",
                      }}
                    >
                      Motivo da rejeição
                    </div>
                    <div className={classes.reviewValue}>
                      {template.rejected_reason}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div className={analyticsClasses.rangePill}>
                    Últimos 30 dias
                  </div>
                  <button
                    type="button"
                    className={classes.btnGhost}
                    onClick={loadAnalytics}
                    disabled={analyticsLoading}
                    style={{ padding: "6px 10px", fontSize: "0.8rem" }}
                  >
                    <RefreshCw
                      size={13}
                      strokeWidth={1.5}
                      style={{
                        transition: "transform 0.4s ease",
                        transform: analyticsLoading
                          ? "rotate(180deg)"
                          : "none",
                      }}
                    />
                    Atualizar
                  </button>
                </div>

                {analyticsLoading && !analytics && (
                  <div className={analyticsClasses.muted}>
                    Carregando estatísticas...
                  </div>
                )}

                {analyticsError && (
                  <div
                    className={classes.reviewSection}
                    style={{
                      borderColor: isDark
                        ? "rgba(245,158,11,0.25)"
                        : "rgba(245,158,11,0.2)",
                    }}
                  >
                    <div className={classes.reviewLabel}>
                      Não foi possível carregar
                    </div>
                    <div className={classes.hint}>{String(analyticsError)}</div>
                    <div
                      className={classes.hint}
                      style={{ marginTop: 6, opacity: 0.75 }}
                    >
                      Dica: a Meta exige que o app tenha permissão
                      `whatsapp_business_management` e que o template já tenha
                      sido enviado para coletar métricas.
                    </div>
                  </div>
                )}

                {analytics && !analyticsError && (
                  <>
                    <div className={analyticsClasses.metricsGrid}>
                      {METRIC_CONFIG.map((m) => {
                        const val = analytics.totals[m.key] || 0;
                        let ratio = null;
                        if (m.key === "delivered")
                          ratio = computeRatio(
                            val,
                            analytics.totals.sent || 0
                          );
                        if (m.key === "read")
                          ratio = computeRatio(
                            val,
                            analytics.totals.delivered || 0
                          );
                        if (m.key === "clicked")
                          ratio = computeRatio(
                            val,
                            analytics.totals.read || 0
                          );
                        const Icon = m.Icon;
                        return (
                          <div
                            key={m.key}
                            className={analyticsClasses.metricCard}
                          >
                            <div
                              className={analyticsClasses.metricHead}
                              style={{ color: m.color }}
                            >
                              <Icon size={13} strokeWidth={1.75} />
                              {m.label}
                            </div>
                            <div className={analyticsClasses.metricValue}>
                              {val.toLocaleString("pt-BR")}
                            </div>
                            {ratio && (
                              <div className={analyticsClasses.metricRatio}>
                                {ratio}{" "}
                                {m.key === "delivered"
                                  ? "entrega"
                                  : m.key === "read"
                                  ? "leitura"
                                  : "CTR"}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {analytics.series.length >= 2 && (
                      <div className={analyticsClasses.chartWrap}>
                        <div className={analyticsClasses.chartTitle}>
                          Evolução diária
                        </div>
                        <LineChart series={analytics.series} />
                        <div className={analyticsClasses.legend}>
                          {METRIC_CONFIG.map((m) => (
                            <div
                              key={m.key}
                              className={analyticsClasses.legendItem}
                            >
                              <span
                                className={analyticsClasses.legendDot}
                                style={{ background: m.color }}
                              />
                              {m.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {analytics.totals.sent === 0 && (
                      <div className={analyticsClasses.muted}>
                        Este template ainda não teve disparos nos últimos 30 dias.
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {tab === "edit" && (
            <div className={classes.footer}>
              <button
                type="button"
                className={classes.btnGhost}
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={classes.btnPrimary}
                onClick={handleSave}
                disabled={!canSave || saving}
              >
                {saving ? (
                  "Salvando..."
                ) : (
                  <>
                    <Check size={15} strokeWidth={2} /> Salvar alterações
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className={classes.previewCol}>
          <div className={classes.previewTitle}>Pré-visualização</div>
          <TemplatePreview
            components={previewComponents}
            headerMediaUrl={
              state.header?.handle &&
              /^https?:\/\//.test(state.header.handle)
                ? state.header.handle
                : null
            }
          />
          {tab === "edit" && bodyVars.length > 0 && (
            <div className={classes.previewMeta}>
              As variáveis serão substituídas pelos dados reais do contato no
              envio.
            </div>
          )}
          {tab === "analytics" && (
            <div className={classes.previewMeta}>
              As métricas refletem os envios realizados pela conta Meta nos
              últimos 30 dias. A Meta sincroniza números com um pequeno atraso.
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default TemplateEditModal;
